import { useState, useRef, useEffect, memo } from "react";
import CustomTooltip from "../Buttons/CustomTooltip";
import { showToast } from "../Buttons/Toaster";
import "./../CSSCustom/toaster.css";
import axios from "axios";
import SourceProgress from "./SourceProgress";

// Defined at module scope so its reference is stable across renders -
// otherwise React treats it as a new component type on every render of
// CreateSource and remounts every instance instead of updating in place.
const TruncatedText = ({ text, maxLength = 18 }) => {
  if (!text) return "N/A";

  const isTruncated = text.length > maxLength;
  const truncated = isTruncated ? `${text.substring(0, maxLength)}...` : text;

  return isTruncated ? (
    <CustomTooltip
      content={text}
      placement="top"
      fallbackPlacements={["top"]}
    >
      <span style={{ cursor: "pointer" }}>{truncated}</span>
    </CustomTooltip>
  ) : (
    <span>{text}</span>
  );
};

function CreateSource({ show, setShow, onSaveCreate }) {
  const [formData, setFormData] = useState({
    jobName: "",
    jobDescription: "",
    sourceType: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [jobId, setJobId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [sourceTypeOptions, setSourceTypeOptions] = useState([]);
  const [jobNameError, setJobNameError] = useState("");
  const [existingJobNames, setExistingJobNames] = useState([]); // Replace with actual fetch if needed
  const [jobCreated, setJobCreated] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [createdJobId, setCreatedJobId] = useState(null);
  const [duplicateJobError, setDuplicateJobError] = useState("");
  const [accessControl, setAccessControl] = useState("upload"); // Default is Upload File
  const [scanOption, setScanOption] = useState("all"); // "all" or "modified"
  const [repoUrl, setRepoUrl] = useState("");
  const [repoToken, setRepoToken] = useState("");
  // Add this new state to CreateSource.js
  const [showProgress, setShowProgress] = useState(false);

  const [selectedTags, setSelectedTags] = useState([
    "ProjectX",
    "Migration.SDX",
    "PG1.1",
  ]);
  const [includeTransformation, setIncludeTransformation] = useState(false);

  const databaseFileTypes = {
    "IBM Datastage": [".xml"],
    Informatica: [".xml"],
    SAS: [".sas"],
    SQL: [".sql"],
    SSIS: [".xml", ".dtsx"],
    Alteryx: [".yxmd"],
    Tableau: [".twb", ".twbx"],
  };

  const validateJobName = (name) => {
    const regex = /^[a-zA-Z0-9 ]+$/; // only letters, numbers, spaces
    return regex.test(name);
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    const fetchSources = async () => {
      const access_token = sessionStorage.getItem("access_token");
      if (!access_token) {
        showToast("Authentication required", "error");
        return;
      }
      const wsBaseSources = process.env.REACT_APP_API_URL_SOURCE_PROFILER;
      try {
        const response = await fetch(`${wsBaseSources}/get_sources`, {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch source types");

        const sources = await response.json();
        const formatted = sources.map((src) => ({
          id: src, // Store original API value as ID
          name: src, // Display name is also the original
          // Add a normalized version for internal use if needed
          normalized: src.toLowerCase().replace(/\s+/g, "_"),
        }));
        setSourceTypeOptions(formatted);
      } catch (error) {
        console.error("Error fetching source types:", error);
        showToast("Unable to load source types", "error");
      }
    };

    fetchSources();
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const simulateUpload = () => {
    if (isUploading) return;
    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);

    setTimeout(() => {
      clearInterval(interval);
      setUploadComplete(true);

      // ✅ Append new files to already uploaded files (fix)
      setUploadedFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const uniqueNewFiles = selectedFiles.filter(
          (f) => !existingNames.has(f.name),
        );
        return [...prev, ...uniqueNewFiles];
      });

      setSelectedFiles([]);
      setIsUploading(false);
      //   showToast("Files uploaded successfully!", "success");
    }, 3500);
  };

  const handleFileValidation = (files) => {
    const allowedExtensions = databaseFileTypes[formData.sourceType] || [];
    const invalidFiles = files.filter((file) => {
      const extension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      showToast(
        `Invalid file type for ${formData.sourceType}. Allowed types: ${
          allowedExtensions.length > 0 ? allowedExtensions.join(", ") : "N/A"
        }`,
        "error",
      );
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!formData.sourceType) {
      showToast("Please select a source type first", "error");
      return;
    }
    if (!handleFileValidation(files)) return;

    setUploadProgress(0);
    setUploadComplete(false);

    // ✅ Append new files, avoid duplicates
    setSelectedFiles((prevFiles) => {
      const existingNames = new Set(prevFiles.map((f) => f.name));
      const uniqueNewFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prevFiles, ...uniqueNewFiles];
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (!formData.sourceType) {
      showToast("Please select a source type first", "error");
      return;
    }
    if (!handleFileValidation(files)) return;

    setUploadProgress(0);
    setUploadComplete(false);

    // ✅ Append new files, avoid duplicates
    setSelectedFiles((prevFiles) => {
      const existingNames = new Set(prevFiles.map((f) => f.name));
      const uniqueNewFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prevFiles, ...uniqueNewFiles];
    });
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const removeUploadedFile = (index) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "sourceType" && value !== formData.sourceType) {
      setSelectedFiles([]);
      setUploadedFiles([]);
      setUploadComplete(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDescriptionChange = (e) => {
    const limitedText = e.target.value.slice(0, 500);
    setFormData((prev) => ({ ...prev, jobDescription: limitedText }));
  };

  const clearForm = () => {
    setFormData({
      jobName: "",
      jobDescription: "",
      sourceType: "",
    });
    setSelectedFiles([]);
    setUploadedFiles([]);
    setUploadComplete(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // const handleClose = () => {
  //   clearForm();
  //   setShow(false);
  // };

  // In CreateSource component
  // Add this function to handle saving as draft
  const saveAsDraft = async (jobId) => {
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token missing", "error");
        return false;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/draft_job?job_id=${jobId}`,
        null,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      if (response.status === 200) {
        showToast("Job saved to draft", "success");
        return true;
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      showToast("Failed to save job to draft", "error");
      return false;
    }
  };

  // Modify the handleClose function
  const handleClose = async (shouldSaveDraft = true) => {
    if (shouldSaveDraft && jobId) {
      await saveAsDraft(jobId);
    }

    // Reset all state
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setDuplicateJobError(null);
    setShow(false);
    setTimeout(() => {
      setCurrentStep(1);
      setFormData({
        jobName: "",
        jobDescription: "",
        sourceType: "",
      });
      setSelectedFiles([]);
      setUploadedFiles([]);
      setUploadComplete(false);
      setJobId(null);
      setIsUploading(false);
      setUploadProgress(0);
    }, 300);
  };

  // Modify the handleSave function to set jobId on successful creation
  const handleSave = async () => {
    if (!uploadComplete || uploadedFiles.length === 0) {
      showToast("Please upload files before saving.", "error");
      return;
    }

    const authToken = sessionStorage.getItem("access_token");
    if (!authToken) {
      showToast("Authentication required", "error");
      return;
    }

    setIsCreatingJob(true);

    const form = new FormData();
    uploadedFiles.forEach((file) => {
      form.append("files", file);
    });

    const queryParams = new URLSearchParams({
      job_name: formData.jobName,
      job_description: formData.jobDescription,
      source_db: formData.sourceType,
    });

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL_SOURCE_PROFILER
        }/create_job?${queryParams.toString()}`,
        {
          method: "POST",
          body: form,
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        if (data?.detail === "Job already exists with the same name.") {
          setDuplicateJobError(
            "A job with this name already exists. Please choose a different name.",
          );
          setIsCreatingJob(false);
          return;
        }
        throw new Error("Upload failed.");
      }

      setDuplicateJobError("");
      if (data?.job_id) {
        setJobId(data.job_id); // Already existing
        setCreatedJobId(data.job_id); // ✅ Needed for scanJob to work
        showToast("Job created successfully!", "success");
        setJobCreated(true);
        setIsCreatingJob(false);
      }
    } catch (error) {
      console.error("API Error:", error);
      showToast("Failed to save job. Please try again.", "error");
      setIsCreatingJob(false);
    }
  };

  //  const scanJob = async () => {
  //   if (!createdJobId) {
  //     showToast("No job ID to scan.", "error");
  //     return;
  //   }

  //   try {
  //     const access_token = sessionStorage.getItem("access_token");
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/scan?job_ids=${createdJobId}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${access_token}`,
  //         },
  //       }
  //     );

  //     if (!response.ok) throw new Error("Scan API failed");

  //     // Show progress view instead of closing
  //     setShowProgress(true);
  //     showToast("Scan initiated successfully!", "success");
  //   } catch (err) {
  //     console.error("Error scanning job:", err);
  //     showToast("Failed to initiate scan", "error");
  //   }
  // };

  const scanJob = async () => {
    if (!createdJobId) {
      showToast("No job ID to scan.", "error");
      return;
    }

    // Immediately show progress view without waiting for API response
    setShowProgress(true);
    showToast("Scan initiated successfully!", "success");

    try {
      const access_token = sessionStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/scan?job_ids=${createdJobId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Scan API failed");
      }

      // The progress view will handle showing updates via WebSocket
    } catch (err) {
      console.error("Error scanning job:", err);
      // Errors will be shown in the progress view via WebSocket
    }
  };
  const isButtonDisabled = () => {
    if (isUploading || isCreatingJob) return true;
    if (uploadComplete && !jobCreated) return !isReadyToSave;
    if (uploadComplete && jobCreated) return false;
    return !isReadyToUpload;
  };

  const isReadyToUpload = formData.sourceType && selectedFiles.length > 0;

  const isReadyToSave =
    formData.jobName && formData.sourceType && uploadedFiles.length > 0;

  return (
    <div
      className={`right-bar3 p-3 ${show ? "d-block" : "d-none"}`}
      style={{ width: "auto" }}
    >
      {showProgress ? (
        <SourceProgress
          jobId={createdJobId}
          jobName={formData.jobName}
          onClose={() => {
            setShowProgress(false);
            handleClose(false);
            onSaveCreate(createdJobId);
          }}
          onMinimize={() => {
            setShowProgress(false);
            handleClose(false);
            onSaveCreate(createdJobId);
          }}
        />
      ) : (
        <div>
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="font-size-13 mb-0 fw-medium text-dark">
              Create Source Profile
            </h6>
            <CustomTooltip content="Close" placement="left">
              <i
                onClick={handleClose}
                className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
              />
            </CustomTooltip>
          </div>

          <div className="trace-label mt-1 pe-3 d-flex align-items-center mb-0 w-fit-content">
            <img
              src="/assets/images/trace_label.png"
              className="w-14px me-2"
              alt="Job icon"
            />
            <h6 className="font-size-10 mb-0 text-dark">
              Input repository settings and run a scan to profile source system
            </h6>
          </div>

          <div
            className="mt-3"
            style={{
              maxHeight: "calc(100vh - 134px)",
              overflowY: "auto",
              padding: "0 12px",
              margin: "0 -14px",
            }}
          >
            {duplicateJobError && (
              <div
                className="text-danger font-size-10"
                style={{ marginBottom: "4px", marginTop: "-1px" }}
              >
                <i className="fas fa-exclamation-circle me-2"></i>
                {duplicateJobError}
              </div>
            )}

            <h6 className="fw-sbold font-size-12">
              Job Name <span style={{ color: "red" }}>*</span>
            </h6>
            <input
              type="text"
              name="jobName"
              placeholder="Enter job name"
              className={`form-control highlighted-input font-size-12  mt-2 colorfor-placeholder styled-input ${
                duplicateJobError || jobNameError ? "is-invalid mb-1" : "mb-3"
              }`}
              style={{
                ...(duplicateJobError || jobNameError
                  ? { border: "1px solid #dc3545" }
                  : {}),
              }}
              value={formData.jobName}
              onChange={(e) => {
                const newName = capitalizeFirstLetter(e.target.value);

                // ✅ Reset errors on typing
                setDuplicateJobError(""); // <-- ✅ ADD THIS LINE

                if (newName === "") {
                  setFormData((prev) => ({ ...prev, jobName: "" }));
                  setJobNameError("");
                  return;
                }

                if (!validateJobName(newName)) {
                  setJobNameError(
                    "Only letters, numbers, and spaces are allowed.",
                  );
                } else if (
                  existingJobNames
                    .map((n) => n.toLowerCase())
                    .includes(newName.toLowerCase())
                ) {
                  setJobNameError("Job name already exists.");
                } else {
                  setJobNameError("");
                }

                setFormData((prev) => ({ ...prev, jobName: newName }));
              }}
            />

            {jobNameError && (
              <div
                className="text-danger font-size-10"
                style={{ marginTop: "-2px", marginBottom: "5px" }}
              >
                {jobNameError}
              </div>
            )}

            <h6 className="fw-sbold font-size-12">Job Description</h6>
            <textarea
              rows="5"
              name="jobDescription"
              value={formData.jobDescription}
              onChange={handleDescriptionChange}
              placeholder="Description"
              className="form-control highlighted-input colorfor-placeholder styled-input font-size-12 w-100 mb-3"
              style={{ height: "80px" }}
            />
            <div
              className="d-flex flex-justify-start font-size-10 text-muted text-end"
              style={{ marginTop: "-15px" }}
            >
              {formData.jobDescription.length}/500 characters
            </div>

            <h6 className="fw-sbold font-size-12 mt-2">
              Source Type <span style={{ color: "red" }}>*</span>
            </h6>
            <select
              className="form-control highlighted-input font-size-12  mt-2 mb-3 colorfor-placeholder styled-input"
              name="sourceType"
              value={formData.sourceType}
              onChange={handleInputChange}
              style={{
                borderRadius: "4px",
                padding: "8px 12px",
                fontSize: "12px",
                width: "100%",
                appearance: "none",
                backgroundImage:
                  "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
                backgroundSize: "12px",
              }}
            >
              <option value="">Select a source type</option>
              {sourceTypeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {" "}
                  {/* Use original API value */}
                  {option.name} {/* Display original name */}
                </option>
              ))}
            </select>
            {/* Access Control Section */}
            {/* <h6 className="fw-sbold font-size-12 mt-2">Access Control</h6>
          <div className="d-flex gap-4 mb-2 mt-1">
            <div className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center">
              <span className="font-size-12 me-2">Direct Connection</span>
              <input
                className="custom-radio"
                type="radio"
                name="accessControl"
                value="direct"
                checked={accessControl === "direct"}
                onChange={() => setAccessControl("direct")}
              />
            </div>

            <div className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center">
              <span className="font-size-12 me-2">Upload File</span>
              <input
                className="custom-radio"
                type="radio"
                name="accessControl"
                id="uploadFile"
                value="upload"
                checked={accessControl === "upload"}
                onChange={() => setAccessControl("upload")}
              />
            </div>
            <div className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center">
              <span className="font-size-12 me-2">Repository URL</span>
              <input
                className="custom-radio"
                type="radio"
                name="accessControl"
                id="repositoryUrl"
                value="repository"
                checked={accessControl === "repository"}
                onChange={() => setAccessControl("repository")}
              />
            </div>
          </div> */}

            {accessControl === "upload" ? (
              <>
                <h6 className="fw-sbold font-size-12 mb-2">Upload Documents</h6>
                <div
                  className="upload-file p-3 mt-2 position-relative"
                  onClick={() => {
                    if (!formData.sourceType) {
                      showToast("Please select a source type first", "error");
                      return;
                    }
                    fileInputRef.current?.click();
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  style={{
                    border: "2px dashed #ccc",
                    minHeight: "150px",
                    cursor: formData.sourceType ? "pointer" : "not-allowed",
                    opacity: formData.sourceType ? 1 : 0.5,
                  }}
                >
                  <div className="text-center">
                    <img
                      src="assets/images/upload.gif"
                      className="w-100px"
                      alt="Upload icon"
                    />
                    <h6 className="text-dark fw-medium text-center mb-1">
                      Drag and Drop files here or click to browse
                    </h6>
                    <p className="font-size-10 text-center mb-2">
                      {formData.sourceType
                        ? databaseFileTypes[formData.sourceType]
                          ? `Supported files: ${databaseFileTypes[
                              formData.sourceType
                            ].join(", ")}`
                          : "Supported files: N/A"
                        : "Please select a source type first to see supported file types"}
                    </p>
                  </div>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept={
                    formData.sourceType &&
                    databaseFileTypes[formData.sourceType]
                      ? databaseFileTypes[formData.sourceType].join(",")
                      : undefined
                  }
                  style={{ display: "none" }}
                />

                {!uploadComplete && selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <h6 className="fw-sbold font-size-12 mb-2">
                      Selected Files
                    </h6>
                    <ul className="list-unstyled">
                      {selectedFiles.map((file, index) => (
                        <li key={index} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="font-size-10">
                              <TruncatedText text={file.name} maxLength={25} />{" "}
                              ({formatFileSize(file.size)})
                            </span>
                            <span
                              className="font-size-12 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(index);
                              }}
                            >
                              ×
                            </span>
                          </div>
                          <div className="progress custom-progress mt-1">
                            <div
                              className="progress-bar"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadComplete && uploadedFiles.length > 0 && (
                  <div className="mt-3">
                    <h6 className="fw-sbold font-size-12 mb-2">
                      Uploaded Files
                    </h6>
                    <ul className="list-unstyled">
                      {uploadedFiles.map((file, index) => (
                        <li key={`uploaded-${index}`} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="font-size-10">
                              <TruncatedText text={file.name} maxLength={35} />{" "}
                              ({formatFileSize(file.size)})
                            </span>
                            <span
                              className="font-size-12 cursor-pointer"
                              onClick={() => removeUploadedFile(index)}
                            >
                              ×
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : accessControl === "direct" ? (
              <>
                <h6 className="fw-sbold font-size-12 mb-2">Host</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="IP address or domain name of the source system"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Port</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="Based on the source DB or ETL platform"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Username</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="Admin or read-only credential"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Password</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="password"
                  placeholder="********"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Repository Path</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="/opt/ibm/datastage/jobs/repo1"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Repository Name</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="ETL_Prod_Repo01"
                />
              </>
            ) : accessControl === "repository" ? (
              <>
                <h6 className="fw-sbold font-size-12 mb-2">Repository URL</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="text"
                  placeholder="https://your-repository-url.com"
                />

                <h6 className="fw-sbold font-size-12 mb-2">Token / API Key</h6>
                <input
                  className="form-control highlighted-input font-size-12  mb-3 styled-input"
                  type="password"
                  placeholder="***************"
                />
              </>
            ) : null}
            <div className="mt-3"></div>

            {/* <h6 className="fw-sbold font-size-12 mb-2">Optional Settings</h6> */}

            {/* <div className="d-flex gap-3 mb-3">
            <div className="border rounded p-2 d-flex align-items-center gap-2">
              <input
                className="custom-checkbox"
                type="checkbox"
                id="scanAll"
                checked={scanOption === "all"}
                onChange={() => setScanOption("all")}
              />
              <label
                className="form-check-label font-size-12 mb-0"
                htmlFor="scanAll"
              >
                Scan All Jobs
              </label>
            </div>

            <div className="border rounded p-2 d-flex align-items-center gap-2">
              <input
                className="custom-checkbox"
                type="checkbox"
                id="onlyModified"
                checked={scanOption === "modified"}
                onChange={() => setScanOption("modified")}
              />
              <label
                className="form-check-label font-size-12 mb-0"
                htmlFor="onlyModified"
              >
                Only Modified Jobs
              </label>
            </div>
          </div> */}

            {/* <div className="mb-3">
            <h6 className="fw-sbold font-size-12 mb-2">Tags / Project</h6>
            <div className="border rounded p-3 mb-3">
              <div className="d-flex flex-wrap gap-2">
                {selectedTags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="badge bg-light text-dark font-size-10 py-1 px-2 rounded-pill border d-flex align-items-center"
                  >
                    {tag}
                    <span
                      className="ms-2 cursor-pointer"
                      onClick={() =>
                        setSelectedTags((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      &times;
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div> */}

            {/* <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{ width: "380px" }}
          >
            <label
              htmlFor="includeTransformation"
              className="font-size-12 mb-0 "
              style={{ marginBottom: 0 }}
            >
              Include Transformation Types
            </label>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeTransformation"
                checked={includeTransformation}
                onChange={() =>
                  setIncludeTransformation(!includeTransformation)
                }
                style={{ width: "32px", height: "18px" }} 
              />
            </div>
          </div> */}
          </div>

          <div className="button-container mt-3">
            <button
              type="button"
              className="btn btn-closebtn custom-button"
              onClick={handleClose}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary custom-button"
              onClick={
                uploadComplete
                  ? jobCreated
                    ? scanJob
                    : handleSave
                  : simulateUpload
              }
              disabled={isButtonDisabled()}
            >
              {uploadComplete
                ? jobCreated
                  ? "Scan"
                  : isCreatingJob
                    ? "Creating Job"
                    : "Next"
                : isUploading
                  ? "Uploading"
                  : "Upload Files"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(CreateSource);
